import { Divider, Form, Spin } from "antd";
import { useAppDispatch } from "core/utils";
import i18n from "i18next";
import { type FC, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ActionBar } from "../../../../components/ActionBar/ActionBar";
import { PageTitle } from "../../../../components/AppLayout/components/Content/components";
import { NoAccessLayout } from "../../../../components/AppLayout/components/Content/components/NoAccessLayout";
import { PageLayout } from "../../../../components/AppLayout/components/Content/components/PageLayout";
import { SecondaryButton } from "../../../../components/Buttons/components/ActionButton";
import { showSuccessNotification } from "../../../../components/NotificationAlerts";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { canAccess } from "../../../../core/router/auth/permissions";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { createDownloadFile } from "../../../../core/utils/CreateDownloadFile";
import { ApplicationConfigurations } from "../components/appConfigsPanelComponent/ApplicationConfigurations";
import { mapCollapseModelToFormModel, mapFormModelToCollapseModel } from "../components/appConfigsUtils";
import { AppConfigImportButton } from "../components/buttons/AppConfigImportButton";
import { useLoadAppConfigQuery, useSaveAppConfigMutation } from "../services/server/AppConfigsApi";
import type { FormJson } from "../types/AppConfigsTypes";

export interface ApplicationConfigurationsPageProps {}

const EXPORT_FILENAME = "apa-config.export";

export const ApplicationConfigurationsPage: FC<ApplicationConfigurationsPageProps> = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const hasAccess = canAccess(ResourceName.APP_CONFIGURATIONS, "canRead");
    const hasWriteAccess = canAccess(ResourceName.APP_CONFIGURATIONS, "canWrite");

    const [saveAppConfigs, { isLoading: saveFormIsInProgress }] = useSaveAppConfigMutation();

    const dataGroup = getAssignedDataGroup();

    const { data: configData, isFetching: configIsFetching } = useLoadAppConfigQuery({ dataGroupCode: dataGroup?.code || "" });
    const jsonModelData = configData?.applicationConfiguration;

    useEffect(() => {
        const collapseModelStateData = mapFormModelToCollapseModel(jsonModelData as FormJson, t);
        form.setFieldsValue({ applicationConfigurations: collapseModelStateData });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, jsonModelData]);

    const onSubmit = async () => {
        const applicationConfigurations = form.getFieldValue("applicationConfigurations");

        if (jsonModelData && applicationConfigurations) {
            const result = await saveAppConfigs(mapCollapseModelToFormModel(jsonModelData, applicationConfigurations));
            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: ResourceName.APP_CONFIGURATIONS.replace(/-/g, " "),
                        }),
                        autoCloseDisabled: false,
                    }),
                );
            }
        }
    };

    const onExportCallback = async () => {
        if (jsonModelData) {
            let configurationData = jsonModelData;

            const applicationConfigurations = form.getFieldValue("applicationConfigurations");

            if (applicationConfigurations) {
                const currentConfig = mapCollapseModelToFormModel(jsonModelData, applicationConfigurations);
                if (currentConfig) {
                    configurationData = currentConfig;
                }
            }
            createDownloadFile(EXPORT_FILENAME, JSON.stringify(configurationData));
        }
    };

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            <PageLayout>
                {hasAccess ? (
                    <>
                        <PageTitle translationKey="menu.appConfigs" />
                        <Divider />
                        <Form form={form} initialValues={{}} component={false}>
                            {hasWriteAccess && (
                                <ActionBar className="left">
                                    <SecondaryButton key="submit" onClick={onSubmit} disabled={false}>
                                        {i18n.t("common.save")}
                                    </SecondaryButton>
                                    <SecondaryButton onClick={onExportCallback} disabled={false}>
                                        {i18n.t("button.export")}
                                    </SecondaryButton>
                                    <Form.Item name="applicationConfigurations">
                                        <AppConfigImportButton />
                                    </Form.Item>
                                </ActionBar>
                            )}
                            <Form.Item name="applicationConfigurations">
                                <ApplicationConfigurations />
                            </Form.Item>
                        </Form>
                    </>
                ) : (
                    <NoAccessLayout />
                )}
            </PageLayout>
        </Spin>
    );
};
